import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { Btn, Field, TextInput, Select } from "@/components/ui-lite";
import { toast } from "sonner";

export default function AdminReconciliationPage() {
  const [cfg, setCfg] = useState({ tally_tolerance_pct: 5, tally_tolerance_usd: 500, tally_rule: "min" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings/reconciliation").then((r) => { setCfg(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    try {
      await api.put("/settings/reconciliation", {
        tally_tolerance_pct: parseFloat(cfg.tally_tolerance_pct),
        tally_tolerance_usd: parseFloat(cfg.tally_tolerance_usd),
        tally_rule: cfg.tally_rule,
      });
      toast.success("Reconciliation settings saved");
    } catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <AppShell title="Reconciliation Settings" subtitle="Configure when a provider invoice is considered a match vs a mismatch">
      <div className="wv-card p-8 max-w-2xl">
        <div className="wv-overline">Tolerance</div>
        <div className="text-sm text-[var(--wv-text-2)] mt-1 mb-6">
          An invoice is <strong>OK to pay</strong> if the absolute USD difference from our computed provider share is within the allowed threshold. The threshold is derived from both a percentage and an absolute cap.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Tolerance % of computed"><TextInput data-testid="recon-pct" type="number" step="0.1" value={cfg.tally_tolerance_pct} onChange={(e) => setCfg({ ...cfg, tally_tolerance_pct: e.target.value })} className="font-mono"/></Field>
          <Field label="Absolute cap (USD)"><TextInput data-testid="recon-usd" type="number" step="1" value={cfg.tally_tolerance_usd} onChange={(e) => setCfg({ ...cfg, tally_tolerance_usd: e.target.value })} className="font-mono"/></Field>
          <Field label="Combine rule">
            <Select data-testid="recon-rule" value={cfg.tally_rule} onChange={(e) => setCfg({ ...cfg, tally_rule: e.target.value })}>
              <option value="min">min — stricter (whichever cap is smaller)</option>
              <option value="max">max — looser (whichever cap is larger)</option>
            </Select>
          </Field>
        </div>
        <div className="mt-6 wv-card p-4 bg-[var(--wv-surface-alt)]/50">
          <div className="wv-overline">How this works</div>
          <div className="mt-2 text-sm text-[var(--wv-text-2)]">
            With <strong>{cfg.tally_rule}</strong> rule:<br />
            allowed_diff = {cfg.tally_rule}( invoice_usd × {cfg.tally_tolerance_pct}%,  ${cfg.tally_tolerance_usd} )<br /><br />
            Example: on a $100,000 invoice, {cfg.tally_tolerance_pct}% = ${((cfg.tally_tolerance_pct || 0) / 100 * 100000).toLocaleString()} — but capped at ${Number(cfg.tally_tolerance_usd).toLocaleString()}. Real allowed = <strong>${cfg.tally_rule === "min" ? Math.min((cfg.tally_tolerance_pct || 0) / 100 * 100000, cfg.tally_tolerance_usd).toLocaleString() : Math.max((cfg.tally_tolerance_pct || 0) / 100 * 100000, cfg.tally_tolerance_usd).toLocaleString()}</strong>.
          </div>
        </div>
        <div className="mt-6"><Btn data-testid="recon-save" onClick={save}>Save settings</Btn></div>
      </div>
    </AppShell>
  );
}
